import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseDestroy } from 'nera-common/angular';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ConfirmService } from 'app/core/services/confirm.service';
import { ToastService } from 'app/core/services/toast.service';
import { NotificationRowComponent } from 'app/layout/common/notifications/notification-row/notification-row.component';
import { NotificationsService } from 'app/core/services/notifications.service';
import { Notification } from 'app/core/services/notifications/notifications.types';
import {
    STATUS_BUCKET_TEXT,
    statusBucketClasses,
} from 'app/shared/constants/status-bucket.constants';
import { LoadingStateComponent } from 'app/shared/components/loading-state/loading-state.component';

type PanelFilter = 'all' | 'unread';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        LoadingStateComponent,
        NgClass,
        NotificationRowComponent,
        TranslocoModule,
    ],
})
export class NotificationsComponent extends BaseDestroy implements OnInit, OnDestroy {
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _notificationsService = inject(NotificationsService);
    private readonly _overlay = inject(Overlay);
    private readonly _viewContainerRef = inject(ViewContainerRef);
    private readonly _router = inject(Router);
    private readonly _toast = inject(ToastService);
    private readonly _i18n = inject(TranslocoService);
    private readonly _confirm = inject(ConfirmService);

    @ViewChild('notificationsOrigin')
    private readonly _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel')
    private readonly _notificationsPanel: TemplateRef<unknown>;

    notifications: Notification[] = [];
    unreadCount = 0;
    hasNewRealtime = false;
    isLoading = false;
    loadError: string | null = null;
    panelFilter: PanelFilter = 'all';
    /** D22: another page exists on the server. */
    hasMore = false;

    private _overlayRef: OverlayRef;
    private _pulseTimer: ReturnType<typeof setTimeout> | null = null;

    get visibleNotifications(): Notification[] {
        if (this.panelFilter === 'unread') {
            return this.notifications.filter((n) => !n.read);
        }
        return this.notifications;
    }

    ngOnInit(): void {
        this._notificationsService.notifications$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((notifications) => {
                this.notifications = notifications;
                this._cdr.markForCheck();
            });

        // PFL-1979 M1: real server-side count, not a derivation from whatever
        // page of notifications$ happens to be loaded (see unreadCount$ doc).
        this._notificationsService.unreadCount$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((count) => {
                this.unreadCount = count;
                this._cdr.markForCheck();
            });

        this._notificationsService.isLoading$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((loading) => {
                this.isLoading = loading;
                this._cdr.markForCheck();
            });

        this._notificationsService.loadError$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((err) => {
                this.loadError = err;
                this._cdr.markForCheck();
            });

        this._notificationsService.hasMore$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((hasMore) => {
                this.hasMore = hasMore;
                this._cdr.markForCheck();
            });

        this._notificationsService.realtimeNotifications$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.hasNewRealtime = true;
                if (this._pulseTimer) clearTimeout(this._pulseTimer);
                this._pulseTimer = setTimeout(() => {
                    this.hasNewRealtime = false;
                    this._cdr.markForCheck();
                }, 2200);
                this._cdr.markForCheck();
            });
    }

    ngOnDestroy(): void {
        if (this._pulseTimer) clearTimeout(this._pulseTimer);
        this._overlayRef?.dispose();
    }

    openPanel(): void {
        if (!this._notificationsPanel || !this._notificationsOrigin) return;
        if (!this._overlayRef) this._createOverlay();
        if (this._overlayRef.hasAttached()) {
            this.closePanel();
            return;
        }
        this._overlayRef.attach(
            new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
        );
        // Soft refresh when opening so the list stays current. getAll()
        // swallows its own errors into loadError$, so there is nothing to
        // report here — but it still needs teardown.
        this._notificationsService
            .getAll()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ error: () => undefined });
    }

    closePanel(): void {
        this._overlayRef?.detach();
    }

    setPanelFilter(filter: PanelFilter): void {
        this.panelFilter = filter;
        this._cdr.markForCheck();
    }

    /** D22: append the next page; the panel is no longer capped at the first 50. */
    loadMore(): void {
        this._notificationsService
            .loadMore()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ error: () => undefined });
    }

    /**
     * D23: the severity chip used to render the raw backend enum ("warning") because
     * this panel never passed `severityLabel`, so `notification-row` fell back to the
     * bare value. Uses the same keys the notifications CENTRE already passes
     * (`my-notifications-list.component.html:44-46`) so both surfaces say one word.
     */
    severityLabel(notification: Notification): string {
        const severity = notification.severity;
        if (!severity) return '';
        return this._i18n.translate(
            `myNotifications.filter.severity.${severity}`
        );
    }

    // D8: mark-all-read reaches the server now, so it can fail; a bare
    // subscribe would drop that into an unhandled error while the rows
    // silently rolled back.
    markAllAsRead(): void {
        this._notificationsService
            .markAllAsRead()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                error: () =>
                    this._toast.error(
                        this._i18n.translate('myNotifications.errors.markRead')
                    ),
            });
    }

    toggleRead(notification: Notification): void {
        this._notificationsService.toggleRead(notification.id);
    }

    // PFL-1840: `delete()` deliberately reports failure. A bare `.subscribe()`
    // dropped that into an unhandled error and the row silently reappeared with
    // no explanation. `event` stays optional because app-notification-row
    // emits the notification alone; its delete button is a sibling of the
    // navigate button rather than nested inside it, so there is nothing left
    // to stop propagating.
    //
    // Kanban #1532: this delete is irreversible (DELETE /me/{id}), so it goes
    // through the app's one confirmation mechanism (ConfirmService, PFL-1864)
    // same as every other destructive action — including the notification
    // centre's own per-row delete (my-notifications-center.component.ts),
    // which already did this; only the bell panel's copy had been missed.
    delete(notification: Notification, event?: Event): void {
        event?.stopPropagation();
        this._confirm.confirmThenDelete({
            titleKey: 'myNotifications.confirmDeleteOneTitle',
            messageKey: 'myNotifications.confirmDeleteOneMessage',
            action$: () => this._notificationsService.deleteNotification(notification.id),
            destroyRef: this.destroyRef,
            onError: () =>
                this._toast.error(
                    this._i18n.translate('myNotifications.errors.remove')
                ),
        });
    }

    navigateTo(notification: Notification): void {
        if (!notification.read) {
            this._notificationsService.markAsRead(notification.id);
        }
        const link = notification.link;
        if (!link) return;

        if (link.startsWith('http://') || link.startsWith('https://')) {
            globalThis.open(link, '_blank', 'noopener');
            this.closePanel();
            return;
        }

        // Prefer in-app navigation for relative paths (even when useRouter was omitted).
        this._router.navigateByUrl(link).catch(() => undefined);
        this.closePanel();
    }

    viewAll(): void {
        this.closePanel();
        this._router.navigateByUrl('/my-notifications').catch(() => undefined);
    }

    retry(): void {
        this._notificationsService
            .getAll()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ error: () => undefined });
    }

    /** Load-failure crest — the danger bucket, not a locally-invented red pair. */
    get loadErrorSurfaceClasses(): string {
        return statusBucketClasses('danger');
    }

    get loadErrorIconClass(): string {
        return STATUS_BUCKET_TEXT.danger;
    }

    trackByFn(_index: number, item: Notification): string {
        return item.id;
    }

    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'notifications-backdrop',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._notificationsOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                        offsetY: 8,
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                        offsetY: -8,
                    },
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                        offsetY: 8,
                    },
                ]),
        });
        this._overlayRef
            .backdropClick()
            .subscribe(() => this._overlayRef.detach());
        this._overlayRef.keydownEvents().subscribe((e) => {
            if (e.key === 'Escape') this.closePanel();
        });
    }
}
